import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw, X, Check, Star, Phone, Mail, Zap } from "lucide-react";

/**
 * ProductDemo - Animated product demo with voiceover
 * Shows a visual walkthrough of the app's key features
 * Matches the calm, warm aesthetic of "Where was i"
 */

// Demo scenes with timing (in seconds) - synced to voiceover (~38 seconds total)
const SCENES = [
  { id: 1, start: 0, end: 3, title: "Landing", description: "You open the page… and just start." },
  { id: 2, start: 3, end: 6, title: "Add Task", description: "Add a task — anything on your mind." },
  { id: 3, start: 6, end: 9, title: "Push to Doing", description: "Push it into Doing when you begin." },
  { id: 4, start: 9, end: 12, title: "Call categorize", description: "If it's a call… it finds its place." },
  { id: 5, start: 12, end: 16, title: "Email categorize", description: "Same with emails — they organize themselves quietly." },
  { id: 6, start: 16, end: 19, title: "Mark Important", description: "Mark what matters. Keep it in view." },
  { id: 7, start: 19, end: 24, title: "Complete Task", description: "When you're done, tap the check. It moves into done." },
  { id: 8, start: 24, end: 27, title: "Bring Back", description: "Need to bring something back? You can." },
  { id: 9, start: 27, end: 32, title: "Find Thread", description: "Just tap Find My Thread. You'll land right where you left off." },
  { id: 10, start: 32, end: 35, title: "Search", description: "There's search at the top, if you need it." },
  { id: 11, start: 35, end: 40, title: "Ending", description: "Anytime you catch yourself asking 'Where was I?' This brings you back." },
];

// Category icon component
const CategoryIcon = ({ category, size = 10 }) => {
  if (category === "call") return <Phone size={size} className="text-violet-500" />;
  if (category === "email") return <Mail size={size} className="text-sky-500" />;
  return null;
};

// Mini task card component for the demo - polished version
const DemoCard = ({ text, category, isImportant, isDone, isActive, delay = 0, isMoving, showCheck, isCompleting, isHighlighted }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 8, scale: 0.95 }}
    animate={{ 
      opacity: 1, 
      y: 0, 
      scale: isMoving ? 1.02 : 1,
      x: isMoving ? [0, -2, 2, 0] : 0
    }}
    exit={{ opacity: 0, scale: 0.9, y: -5 }}
    transition={{ 
      duration: 0.35, 
      delay,
      layout: { duration: 0.4 }
    }}
    className={`
      relative px-3 py-2 rounded-lg text-[13px] leading-snug mb-2 
      transition-all duration-200
      ${isDone 
        ? 'bg-stone-50/80 text-stone-400 border border-stone-100' 
        : 'bg-white border border-stone-200/80 shadow-sm'
      }
      ${isActive && !isDone ? 'ring-2 ring-[#3DC4D0]/60 border-[#3DC4D0]/40 shadow-md shadow-[#3DC4D0]/10' : ''}
      ${isMoving ? 'shadow-lg shadow-stone-200/50' : ''}
      ${isCompleting ? 'ring-2 ring-emerald-400/60 border-emerald-400/40' : ''}
    `}
  >
    {/* Green checkmark at top of card */}
    <AnimatePresence>
      {showCheck && (
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center shadow-md"
        >
          <Check size={12} className="text-white" strokeWidth={3} />
        </motion.div>
      )}
    </AnimatePresence>
    
    {/* Important task highlighter */}
    {isImportant && !isDone && (
      <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">
        <div 
          className="absolute inset-0 bg-gradient-to-r from-amber-200/40 via-yellow-100/30 to-transparent"
          style={{
            clipPath: 'polygon(0 15%, 85% 10%, 80% 90%, 0 85%)',
          }}
        />
      </div>
    )}
    
    <div className={`relative flex items-start gap-1.5 ${isDone ? 'line-through decoration-stone-400' : ''}`}>
      {category && (
        <span className="flex-shrink-0 mt-0.5">
          <CategoryIcon category={category} />
        </span>
      )}
      <span className="flex-1">
        {isHighlighted ? (
          <motion.span
            initial={{ backgroundSize: '0% 100%' }}
            animate={{ backgroundSize: '100% 100%' }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            style={{
              background: 'linear-gradient(to right, rgba(253, 224, 71, 0.5), rgba(253, 224, 71, 0.4))',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'left center',
              padding: '0.1em 0.2em',
              margin: '0 -0.2em',
              borderRadius: '2px',
            }}
          >
            {text}
          </motion.span>
        ) : (
          text
        )}
      </span>
      {isImportant && !isDone && (
        <Star size={10} className="flex-shrink-0 mt-0.5 text-amber-400 fill-amber-400" />
      )}
    </div>
    
    {/* Done date for completed tasks */}
    {isDone && (
      <div className="text-[8px] text-stone-300 mt-1">Yesterday</div>
    )}
  </motion.div>
);

// Mini column component - styled like the real app
const DemoColumn = ({ title, children, highlight, isEmpty }) => (
  <div 
    className={`
      flex-1 min-w-0 rounded-xl p-2.5 transition-all duration-300
      ${title === 'Done' ? 'bg-stone-50/50' : 'bg-[#FAFAF9]'}
      ${highlight ? 'ring-2 ring-[#F58227]/30 bg-orange-50/30' : ''}
    `}
  >
    <div className={`
      text-[10px] font-semibold uppercase tracking-wider mb-2 px-1
      ${title === 'Doing' ? 'text-[#0CBCBF]' : title === 'Done' ? 'text-[#4CAF50]' : 'text-stone-500'}
    `}>
      {title === 'To Do' ? (
        <>To <span className="text-[#4CAF50]">Do</span></>
      ) : (
        title
      )}
    </div>
    <div className="min-h-[80px]">
      <AnimatePresence mode="popLayout">
        {children}
      </AnimatePresence>
      {isEmpty && (
        <div className="text-[9px] text-stone-300 italic px-1 py-4 text-center">
          Empty
        </div>
      )}
    </div>
  </div>
);

// Completion ripple effect
const CompletionRipple = () => (
  <motion.div
    initial={{ scale: 0, opacity: 0.5 }}
    animate={{ scale: 3, opacity: 0 }}
    transition={{ duration: 1.2, ease: "easeOut" }}
    className="absolute top-1/2 left-1/2 w-16 h-16 -ml-8 -mt-8 rounded-full bg-gradient-to-r from-[#3DC4D0]/40 to-[#3DC4D0]/20 pointer-events-none"
  />
);

// Floating particles for completion
const CompletionParticles = () => (
  <>
    {[...Array(5)].map((_, i) => (
      <motion.div
        key={`completion-particle-${i}`}
        initial={{ 
          opacity: 0.8, 
          scale: 0,
          x: 0,
          y: 0 
        }}
        animate={{ 
          opacity: 0, 
          scale: 1,
          x: (Math.random() - 0.5) * 60,
          y: -40 - Math.random() * 30
        }}
        transition={{ 
          duration: 1.5, 
          delay: 0.2 + i * 0.1,
          ease: "easeOut"
        }}
        className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full"
        style={{
          background: i % 2 === 0 ? '#F58227' : '#3DC4D0',
        }}
      />
    ))}
  </>
);

const ProductDemo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentScene, setCurrentScene] = useState(0);
  const [showDemo, setShowDemo] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  
  // Determine current scene based on time
  useEffect(() => {
    const scene = SCENES.findIndex(s => currentTime >= s.start && currentTime < s.end);
    if (scene !== -1 && scene !== currentScene) {
      setCurrentScene(scene);
    }
  }, [currentTime, currentScene]);
  
  // Update time during playback
  useEffect(() => {
    if (isPlaying && audioRef.current) {
      intervalRef.current = setInterval(() => {
        setCurrentTime(audioRef.current?.currentTime || 0);
      }, 100);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying]);
  
  const handlePlay = () => {
    setShowDemo(true);
    setHasEnded(false);
    
    // Scroll so "With audio narration" is at the bottom of screen
    setTimeout(() => {
      const audioNote = document.getElementById("demo-audio-note");
      if (audioNote) {
        const rect = audioNote.getBoundingClientRect();
        const scrollTarget = window.pageYOffset + rect.bottom - window.innerHeight + 170; // +1.5 inch higher
        window.scrollTo({ top: scrollTarget, behavior: "smooth" });
      }
    }, 100);
    
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }, 300);
  };
  
  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };
  
  const handleRestart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setCurrentTime(0);
      setCurrentScene(0);
      setIsPlaying(true);
      setHasEnded(false);
    }
  };
  
  const handleEnded = () => {
    setIsPlaying(false);
    setHasEnded(true);
  };
  
  const handleClose = () => {
    handlePause();
    setShowDemo(false);
    setCurrentTime(0);
    setCurrentScene(0);
    setHasEnded(false);
  };
  
  // Demo state based on scene - progressive state machine
  const getSceneState = () => {
    const scene = currentScene;
    
    // Base state
    const state = {
      todoTasks: [],
      doingTasks: [],
      doneTasks: [],
      showSearch: false,
      searchQuery: '',
      highlightColumn: null,
      showFindThread: false,
      findThreadZoom: false,
      activeTask: null,
      showRipple: false,
      showParticles: false,
      flameCount: 0,
      movingTask: null,
      showCheckOnTask: null,
      completingTask: null,
    };
    
    // Scene 0: Landing - minimal board with one task
    if (scene === 0) {
      state.todoTasks = [
        { id: 1, text: "Review project notes" },
      ];
    }
    
    // Scene 1: Add task - new task appears
    if (scene >= 1) {
      state.todoTasks = [
        { id: 1, text: "Review project notes" },
        { id: 2, text: "Finish report draft", delay: 0.2 },
      ];
    }
    
    // Scene 2: Push to Doing
    if (scene >= 2) {
      state.todoTasks = [{ id: 2, text: "Finish report draft" }];
      state.doingTasks = [{ id: 1, text: "Review project notes", isActive: scene === 2 }];
      state.highlightColumn = scene === 2 ? 'doing' : null;
      state.movingTask = scene === 2 ? 1 : null;
    }
    
    // Scene 3: Call categorize - "Call Dawn" with phone icon
    if (scene >= 3) {
      state.highlightColumn = null;
      state.movingTask = null;
      state.doingTasks = [
        { id: 1, text: "Review project notes" },
        { id: 3, text: "Call Dawn", category: "call", delay: scene === 3 ? 0.3 : 0, isActive: scene === 3 },
      ];
    }
    
    // Scene 4: Email categorize - "Email Ami" with email icon
    if (scene >= 4) {
      state.doingTasks = [
        { id: 1, text: "Review project notes" },
        { id: 3, text: "Call Dawn", category: "call" },
        { id: 4, text: "Email Ami", category: "email", delay: scene === 4 ? 0.3 : 0, isActive: scene === 4 },
      ];
    }
    
    // Scene 5: Mark important
    if (scene >= 5) {
      state.doingTasks = [
        { id: 1, text: "Review project notes", isImportant: true },
        { id: 3, text: "Call Dawn", category: "call" },
        { id: 4, text: "Email Ami", category: "email" },
      ];
    }
    
    // Scene 6: Complete task - show checkmark, then slide to done
    if (scene >= 6) {
      // Show checkmark on the task being completed
      const showingCheck = scene === 6 && currentTime < 21;
      const taskCompleted = scene === 6 && currentTime >= 21;
      
      if (showingCheck) {
        state.showCheckOnTask = 1;
        state.completingTask = 1;
        state.doingTasks = [
          { id: 1, text: "Review project notes", isImportant: true, showCheck: true, isCompleting: true },
          { id: 3, text: "Call Dawn", category: "call" },
          { id: 4, text: "Email Ami", category: "email" },
        ];
      } else {
        state.showRipple = scene === 6;
        state.showParticles = scene === 6;
        state.flameCount = 1;
        state.doingTasks = [
          { id: 3, text: "Call Dawn", category: "call" },
          { id: 4, text: "Email Ami", category: "email" },
        ];
        state.doneTasks = [
          { id: 1, text: "Review project notes", isDone: true },
        ];
      }
    }
    
    // Scene 7: Bring back
    if (scene >= 7) {
      state.showRipple = false;
      state.showParticles = false;
      state.showCheckOnTask = null;
      state.completingTask = null;
      state.todoTasks = [
        { id: 2, text: "Finish report draft" },
        { id: 4, text: "Email Ami", category: "email", delay: scene === 7 ? 0.2 : 0 },
      ];
      state.doingTasks = [
        { id: 3, text: "Call Dawn", category: "call" },
      ];
    }
    
    // Scene 8: Find my thread - show orange button and zoom, highlight Email Ami in TO DO
    if (scene >= 8) {
      state.showFindThread = true;
      state.findThreadZoom = scene === 8;
      state.activeTask = 3;
      state.doingTasks = [
        { id: 3, text: "Call Dawn", category: "call", isActive: true },
      ];
      // Highlight Email Ami in TO DO when showing Find My Thread
      state.todoTasks = [
        { id: 2, text: "Finish report draft" },
        { id: 4, text: "Email Ami", category: "email", isActive: scene === 8 },
      ];
    }
    
    // Scene 9: Search - show "search" in search bar
    if (scene >= 9) {
      state.showFindThread = false;
      state.findThreadZoom = false;
      state.showSearch = true;
      state.searchQuery = "search";
      state.activeTask = null;
      state.doingTasks = [
        { id: 3, text: "Call Dawn", category: "call" },
      ];
      state.todoTasks = [
        { id: 2, text: "Finish report draft" },
        { id: 4, text: "Email Ami", category: "email" },
      ];
    }
    
    // Scene 10: Ending - highlight "Finish report draft" with yellow marker
    if (scene >= 10) {
      state.showSearch = false;
      state.searchQuery = '';
      state.activeTask = null;
      state.todoTasks = [
        { id: 2, text: "Finish report draft", isHighlighted: true },
        { id: 4, text: "Email Ami", category: "email" },
      ];
    }
    
    return state;
  };
  
  const sceneState = getSceneState();
  const progress = Math.min((currentTime / 40) * 100, 100);
  
  return (
    <section 
      id="demo"
      className="py-12 md:py-12 px-6 md:px-12 bg-gradient-to-b from-[#FDFBF7] to-white"
      data-testid="product-demo-section"
    >
      <div className="max-w-2xl mx-auto">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#0CBCBF] mb-3">
            See it in action
          </p>
          <h2 className="text-2xl md:text-3xl font-semibold text-[#27272A] tracking-tight">
            40-second demo
          </h2>
        </motion.div>
        
        {/* Audio element */}
        <audio
          ref={audioRef}
          src="/sounds/demo_voiceover.mp3"
          onEnded={handleEnded}
          preload="auto"
        />
        
        {/* Preview / Play button */}
        <AnimatePresence mode="wait">
          {!showDemo ? (
            <motion.div
              key="preview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98 }}
              className="relative cursor-pointer group"
              onClick={handlePlay}
              data-testid="demo-play-button"
            >
              {/* Preview mockup - styled like real app */}
              <div className="bg-gradient-to-br from-stone-50 to-stone-100/80 rounded-2xl p-5 md:p-6 border border-stone-200/60 shadow-lg shadow-stone-200/30">
                {/* Mini app preview header */}
                <div className="bg-white rounded-xl border border-stone-100 overflow-hidden shadow-sm">
                  {/* Header bar */}
                  <div className="bg-stone-50/80 px-3 py-1.5 border-b border-stone-100 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F58227]" />
                      <span className="text-[9px] font-medium text-stone-500">Where was i</span>
                    </div>
                    <div className="text-[9px] text-stone-400">demo</div>
                  </div>
                  
                  {/* Board preview */}
                  <div className="p-3 flex gap-2">
                    <div className="flex-1 bg-[#FAFAF9] rounded-lg p-2">
                      <div className="text-[8px] font-semibold text-stone-400 uppercase mb-1.5">To <span className="text-[#4CAF50]">Do</span></div>
                      <div className="h-3 w-full bg-white rounded border border-stone-100 mb-1" />
                      <div className="h-3 w-3/4 bg-white rounded border border-stone-100" />
                    </div>
                    <div className="flex-1 bg-[#FAFAF9] rounded-lg p-2">
                      <div className="text-[8px] font-semibold text-[#0CBCBF] uppercase mb-1.5">Doing</div>
                      <div className="h-3 w-full bg-white rounded border border-stone-100 mb-1" />
                    </div>
                    <div className="flex-1 bg-stone-50/50 rounded-lg p-2">
                      <div className="text-[8px] font-semibold text-[#4CAF50] uppercase mb-1.5">Done</div>
                      <div className="h-3 w-2/3 bg-stone-100/50 rounded" />
                    </div>
                  </div>
                </div>
                
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/5 rounded-2xl transition-colors duration-300">
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="bg-[#0CBCBF] text-white px-6 py-3 rounded-full flex items-center gap-2.5 shadow-lg shadow-[#0CBCBF]/25 group-hover:shadow-xl group-hover:shadow-[#0CBCBF]/30 transition-shadow"
                  >
                    <Play size={18} fill="white" />
                    <span className="font-semibold text-sm">Watch how it brings you back</span>
                  </motion.div>
                </div>
              </div>
              
              <p id="demo-audio-note" className="text-center text-sm text-stone-400 mt-4">
                With audio narration
              </p>
            </motion.div>
          ) : (
            /* Active demo view */
            <motion.div
              key="demo"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 0.5, 
                ease: [0.34, 1.56, 0.64, 1], // Bouncy swell effect
                scale: { duration: 0.6 }
              }}
              exit={{ opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl shadow-stone-200/40 border border-stone-200/60 overflow-hidden"
              data-testid="demo-active-view"
            >
              {/* Demo header - mimics real app */}
              <div className="bg-gradient-to-r from-stone-50 to-stone-100/50 px-4 py-2.5 border-b border-stone-200/60 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#F58227]" />
                  <span className="text-xs font-semibold text-stone-600 tracking-tight">Where was i</span>
                </div>
                
                {/* Search bar mock */}
                <AnimatePresence>
                  {sceneState.showSearch && (
                    <motion.div
                      initial={{ opacity: 0, width: 0, scale: 0.9 }}
                      animate={{ opacity: 1, width: 110, scale: 1 }}
                      exit={{ opacity: 0, width: 0, scale: 0.9 }}
                      className="bg-white border border-stone-200 rounded-full px-2.5 py-1 text-[10px] text-stone-500 flex items-center gap-1.5 shadow-sm"
                    >
                      <span className="text-stone-300">🔍</span>
                      <span>{sceneState.searchQuery || "Search..."}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Flame counter */}
                <motion.div 
                  className="flex items-center gap-1"
                  animate={sceneState.showParticles ? { scale: [1, 1.2, 1] } : {}}
                  transition={{ duration: 0.5 }}
                >
                  <span className="text-sm">🔥</span>
                  <motion.span
                    key={sceneState.flameCount}
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-xs font-semibold text-orange-500"
                  >
                    {sceneState.flameCount}
                  </motion.span>
                </motion.div>
              </div>
              
              {/* Demo content - Board view */}
              <motion.div 
                className="p-4 min-h-[220px] relative bg-gradient-to-b from-white to-stone-50/30"
                animate={sceneState.findThreadZoom ? { scale: 1.05 } : { scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                {/* Completion effects */}
                <AnimatePresence>
                  {sceneState.showRipple && <CompletionRipple />}
                  {sceneState.showParticles && <CompletionParticles />}
                </AnimatePresence>
                
                {/* Columns */}
                <div className="flex gap-2.5">
                  <DemoColumn 
                    title="To Do" 
                    isEmpty={sceneState.todoTasks.length === 0}
                  >
                    {sceneState.todoTasks.map(task => (
                      <DemoCard key={task.id} {...task} />
                    ))}
                  </DemoColumn>
                  
                  <DemoColumn 
                    title="Doing" 
                    highlight={sceneState.highlightColumn === 'doing'}
                    isEmpty={sceneState.doingTasks.length === 0}
                  >
                    {sceneState.doingTasks.map(task => (
                      <DemoCard 
                        key={task.id} 
                        {...task} 
                        isActive={task.id === sceneState.activeTask || task.isActive}
                        isMoving={task.id === sceneState.movingTask}
                        showCheck={task.showCheck}
                        isCompleting={task.isCompleting}
                      />
                    ))}
                  </DemoColumn>
                  
                  <DemoColumn 
                    title="Done"
                    isEmpty={sceneState.doneTasks.length === 0}
                  >
                    {sceneState.doneTasks.map(task => (
                      <DemoCard key={task.id} {...task} />
                    ))}
                  </DemoColumn>
                </div>
                
                {/* Find My Thread button - ORANGE */}
                <AnimatePresence>
                  {sceneState.showFindThread && (
                    <motion.div
                      initial={{ opacity: 0, y: 15, scale: 0.95 }}
                      animate={{ 
                        opacity: 1, 
                        y: 0, 
                        scale: sceneState.findThreadZoom ? 1.15 : 1 
                      }}
                      exit={{ opacity: 0, y: 5 }}
                      transition={{ duration: 0.4 }}
                      className="absolute bottom-3 left-1/2 -translate-x-1/2"
                    >
                      <motion.div 
                        className="bg-[#F58227] text-white text-[11px] font-medium px-4 py-1.5 rounded-full shadow-lg shadow-[#F58227]/30 flex items-center gap-1.5"
                        animate={sceneState.findThreadZoom ? { 
                          boxShadow: ['0 10px 15px -3px rgba(245, 130, 39, 0.3)', '0 10px 25px -3px rgba(245, 130, 39, 0.5)', '0 10px 15px -3px rgba(245, 130, 39, 0.3)']
                        } : {}}
                        transition={{ duration: 1, repeat: sceneState.findThreadZoom ? Infinity : 0 }}
                      >
                        <Zap size={12} />
                        Find my thread
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
              
              {/* Controls and caption bar - minimal */}
              <div className="bg-[#E8F6F6] px-4 py-2">
                <div className="flex items-center justify-between">
                  {/* Playback controls */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={isPlaying ? handlePause : (hasEnded ? handleRestart : handlePlay)}
                      className="p-1.5 hover:bg-[#0CBCBF]/10 rounded-full transition-colors text-stone-500"
                      data-testid="demo-play-pause"
                    >
                      {isPlaying ? <Pause size={16} /> : <Play size={16} fill="currentColor" />}
                    </button>
                    <button
                      onClick={handleRestart}
                      className="p-1.5 hover:bg-[#0CBCBF]/10 rounded-full transition-colors text-stone-500"
                      data-testid="demo-restart"
                    >
                      <RotateCcw size={13} />
                    </button>
                  </div>
                  
                  {/* Progress bar and caption inline */}
                  <div className="flex-1 mx-3 flex items-center gap-3">
                    <div className="flex-1">
                      <div className="h-[3px] bg-[#0CBCBF]/20 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-[#0CBCBF]/60"
                          style={{ width: `${progress}%` }}
                          transition={{ duration: 0.1 }}
                        />
                      </div>
                    </div>
                    <motion.span 
                      key={currentScene}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-[13px] text-stone-400 whitespace-nowrap max-w-[260px] truncate"
                    >
                      {hasEnded ? "This brings you back." : (SCENES[currentScene]?.description || '')}
                    </motion.span>
                  </div>
                  
                  {/* Close button */}
                  <button
                    onClick={handleClose}
                    className="p-1.5 hover:bg-[#0CBCBF]/10 rounded-full transition-colors text-stone-400 hover:text-stone-500"
                    data-testid="demo-close"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ProductDemo;
